import type { Stats, Track, PlaybackMode } from './types.js';
import { findElement } from './dom.js';
import { acquireWakeLock, releaseWakeLock } from './wakelock.js';
export function escapeHtml(str: string | null | undefined) {
  return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

let seekToastTimeout: ReturnType<typeof setTimeout> | undefined;

export function showSeekToast(seconds: number | string) {
  const toast = findElement('seek-toast', HTMLElement);
  if (!toast) return;
  toast.textContent = typeof seconds === 'string' ? seconds : seconds < 0 ? `⏪ ${seconds}s` : `⏩ +${seconds}s`;
  toast.classList.add('active');
  clearTimeout(seekToastTimeout);
  seekToastTimeout = setTimeout(() => {
    toast.classList.remove('active');
  }, 700);
}

export function updateStatsUI(stats: Stats | null | undefined, playbackMode: PlaybackMode) {
  if (!stats) return;
  const statLiked = findElement('stat-liked', HTMLElement);
  const statDisliked = findElement('stat-disliked', HTMLElement);
  const statUnrated = findElement('stat-unrated', HTMLElement);
  const statTotal = findElement('stat-total', HTMLElement);
  const barLiked = findElement('bar-liked', HTMLElement);
  const barDisliked = findElement('bar-disliked', HTMLElement);
  const modeLikedCount = findElement('mode-liked-count', HTMLElement);
  const tabCountLiked = findElement('tab-count-liked', HTMLElement);
  const tabCountDisliked = findElement('tab-count-disliked', HTMLElement);

  if (statLiked) statLiked.textContent = String(stats.liked);
  if (statDisliked) statDisliked.textContent = String(stats.disliked);

  const unratedVal = playbackMode === 'local' ? stats.localUnrated : stats.unrated;
  const totalVal = playbackMode === 'local' ? stats.localTotal : stats.total;

  if (statUnrated) statUnrated.textContent = String(unratedVal);
  if (statTotal) statTotal.textContent = String(totalVal);

  if (tabCountLiked) tabCountLiked.textContent = String(stats.liked);
  if (tabCountDisliked) tabCountDisliked.textContent = String(stats.disliked);
  if (modeLikedCount) modeLikedCount.textContent = String(stats.liked);

  if (stats.total > 0 && barLiked && barDisliked) {
    const likedPct = (stats.liked / stats.total) * 100;
    const dislikedPct = (stats.disliked / stats.total) * 100;
    barLiked.style.width = `${likedPct}%`;
    barDisliked.style.width = `${dislikedPct}%`;
  }
}

export function formatSourceLabel(source: string | null, isLocal: boolean) {
  if (!source) {
    return isLocal ? '💾 Local Download' : '🌐 Catalog';
  }
  const s = source.toLowerCase();
  if (s.includes('tracks_single') || s.includes('tracks.txt')) {
    return '🎯 Single Track (tracks.txt)';
  }
  if (s === 'seed_tracks' || s.includes('youtube_music_matched.json')) {
    return '🎧 Liked / Seed';
  }
  if (s === 'related_tracks' || s.includes('related')) {
    return '📻 Related Tracks';
  }
  if (s === 'output/playlists_all.json' || s.includes('playlist')) {
    return '📜 Playlist';
  }
  if (s === 'downloads') {
    return '💾 Local Download';
  }
  if (source.startsWith('output/')) {
    return `📁 ${source.replace('output/', '')}`;
  }
  return `📦 ${source}`;
}

export function displayTrack(track: Track | null, currentUser: string | null, playbackMode: PlaybackMode, updateUrl = true) {
  if (!track) return;
  const trackTitle = findElement('track-title', HTMLElement);
  const trackArtist = findElement('track-artist', HTMLElement);
  const trackIdBadge = findElement('track-id-badge', HTMLElement);
  const trackSourceBadge = findElement('track-source-badge', HTMLElement);
  const ytLink = findElement('yt-link', HTMLAnchorElement);
  const audioContainer = findElement('audio-container', HTMLElement);
  const audioPlayer = findElement('audio-player', HTMLAudioElement);
  const ytContainer = findElement('yt-container', HTMLElement);
  const ytPlayer = findElement('yt-player', HTMLIFrameElement);

  if (trackTitle) trackTitle.textContent = track.title || 'Untitled Track';
  if (trackArtist) trackArtist.textContent = track.artist || 'Unknown Artist';
  if (trackIdBadge) trackIdBadge.textContent = `YouTube ID: ${track.videoId}`;
  if (trackSourceBadge) {
    trackSourceBadge.textContent = `Source: ${formatSourceLabel(track.source, track.isLocal)}`;
    trackSourceBadge.title = `Origin / Download Source: ${track.source || 'Local Audio'}`;
  }
  if (ytLink) ytLink.href = `https://www.youtube.com/watch?v=${encodeURIComponent(track.videoId)}`;

  const btnNuke = findElement('btn-nuke', HTMLButtonElement);
  const nukeShortcutHint = findElement('nuke-shortcut-hint', HTMLElement);
  const canNuke = currentUser === 'uriel' || currentUser === 'diane';
  const hasLikes = track && Array.isArray(track.likedBy) && track.likedBy.length > 0;

  if (btnNuke) {
    btnNuke.style.display = canNuke ? 'flex' : 'none';
    btnNuke.style.opacity = '';
    btnNuke.disabled = false;
    if (hasLikes) {
      btnNuke.title = `☢️ Attention : déjà liké par ${track.likedBy.join(', ')} (Cliquer pour confirmer la suppression)`;
      console.log(`[Nuke] Morceau "${track.title || track.videoId}" déjà liké par :`, track.likedBy);
    } else {
      btnNuke.title = "☢️ Completely delete track from database (Press X or 4)";
    }
  }

  if (nukeShortcutHint) {
    nukeShortcutHint.style.display = canNuke ? 'inline' : 'none';
    nukeShortcutHint.style.opacity = '1';
  }

  if (updateUrl && track.videoId) {
    const url = new URL(window.location.href);
    url.searchParams.set('v', track.videoId);
    url.searchParams.set('user', currentUser || '');
    window.history.replaceState({ videoId: track.videoId, user: currentUser }, '', url.toString());
  }

  if (track.audioUrl) {
    if (audioContainer) audioContainer.style.display = 'flex';
    if (ytContainer) ytContainer.style.display = 'none';
    if (ytPlayer) ytPlayer.src = '';

    if (audioPlayer) {
      audioPlayer.src = track.audioUrl;
      audioPlayer.play().catch(e => console.log('Autoplay policy paused audio:', e));
    }
  } else {
    if (audioContainer) audioContainer.style.display = 'none';
    if (audioPlayer) {
      audioPlayer.pause();
      audioPlayer.src = '';
    }
    if (ytContainer) ytContainer.style.display = 'block';

    if (ytPlayer) {
      ytPlayer.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(track.videoId)}?autoplay=1&enablejsapi=1&rel=0`;
      acquireWakeLock();
    }
  }
}

export function showFinishedState(currentUser: string | null, playbackMode: PlaybackMode) {
  releaseWakeLock(true);
  const audioPlayer = findElement('audio-player', HTMLAudioElement);
  const ytPlayer = findElement('yt-player', HTMLIFrameElement);
  const playerCard = findElement('player-card', HTMLElement);

  if (audioPlayer) {
    audioPlayer.pause();
    audioPlayer.src = '';
  }
  if (ytPlayer) ytPlayer.src = '';

  const modeText = playbackMode === 'local' ? 'local downloaded' : 'available';
  if (playerCard) {
    playerCard.innerHTML = `
      <div class="finished-card">
        <div class="finished-icon">🎉</div>
        <h2>All ${escapeHtml(modeText)} tracks rated by ${escapeHtml(currentUser)}!</h2>
        <p style="color: var(--text-muted)">You have completed rating tracks in this mode.</p>
        <button class="btn-tab" id="btn-finished-reload" style="padding: 12px 24px; font-size: 1rem;">
          🔄 Refresh / Reload
        </button>
      </div>
    `;
    findElement('btn-finished-reload', HTMLButtonElement)?.addEventListener('click', () => window.location.reload());
  }
}
